import React from 'react'
import i18next from 'i18next'
import { connect } from 'react-redux'
import { Modal, Button, Card } from 'react-bootstrap'
import ActionsDropdown from 'src/components/system_wide/actions_dropdown'
import SearchButton from 'src/components/system_wide/search_button'
import ResponsiveTable from 'src/components/system_wide/responsive_table'
import CategoryForm from './category_form'
import {
  setCurrentCategory,
  createCategory,
  updateCategory,
  deleteCategory,
  toggleShowForm,
  getCategories,
  resetCurrentCategory,
  resetCategories,
  updateErrors,
} from 'src/store/category'
import { Trans } from 'react-i18next'
import { checkCapabilities } from 'src/enhancers/with_capabilities'

const { t } = i18next

const mstp = state => {
  let { total, categories, show_form, current_category, errors } = state.categories
  const can_manage = checkCapabilities(['manage_categories'])
  return { total, categories, show_form, current_category, errors, can_manage }
}

class ManageCategories extends React.Component {
  state = {
    show_modal: false,
    filter: '',
    mode: '',
  }

  tableRef = React.createRef()

  actions = [
    {
      icon: 'fas fa-eye',
      variant: 'outline-primary',
      text: t('actions.view', 'Visualizza'),
      onClick: rowData => {
        this.setState({ mode: 'view' })
        this.props.dispatch(setCurrentCategory({ ...rowData }))
        this.props.dispatch(toggleShowForm())
      },
    },
    {
      icon: 'fas fa-edit',
      variant: 'outline-warning',
      text: t('actions.edit', 'Modifica'),
      visible: () => this.props.can_manage,
      onClick: rowData => {
        this.props.dispatch(setCurrentCategory({ ...rowData }))
        this.props.dispatch(toggleShowForm())
      },
    },
    {
      icon: 'fas fa-trash-alt',
      variant: 'outline-danger',
      text: t('actions.delete', 'Elimina'),
      visible: () => this.props.can_manage,
      onClick: rowData => {
        this.props.dispatch(setCurrentCategory({ ...rowData }))
        this.setState({ show_modal: true })
      },
    },
  ]

  actionsRenderer = ({ rowData }) => {
    return <ActionsDropdown rowData={rowData} actions={this.actions} />
  }

  renderDetails = rowData => {
    let { name, enabled } = rowData

    return (
      <Card className="shadow-lg mb-4 border-0 rounded-lg">
        <Card.Body className="text-center pb-2 pt-2">
          <h4 className="fw-bold text-primary d-flex align-items-center justify-content-center">
            {name}
            <span
              className={`ms-2 rounded-circle ${enabled ? 'bg-success' : 'bg-danger'}`}
              style={{
                width: '12px',
                height: '12px',
                display: 'inline-block',
              }}
              title={enabled ? 'Visibile' : 'Non visibile'}></span>
          </h4>
        </Card.Body>

        <Card.Footer className="text-center bg-light pb-3 pt-3">
          <div className="d-flex justify-content-around flex-wrap">
            {this.actions
              .filter(a => {
                if (typeof a.visible === 'function') {
                  return a.visible(rowData, a)
                }
                return a?.visible ?? true
              })
              .map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant}
                  size="sm"
                  title={action.text}
                  disabled={action?.disabled ?? false}
                  onClick={() => action.onClick(rowData)}>
                  <i className={`${action.icon} me-0`}></i>
                </Button>
              ))}
          </div>
        </Card.Footer>
      </Card>
    )
  }

  columns = [
    {
      title: '',
      className: 'd-none d-sm-block text-center',
      style: { width: '6%', minWidth: '3rem' },
      data: this.actionsRenderer,
    },
    {
      title: t('components.categories.name', 'Nome'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '80%' },
      sortable: true,
      data: 'name',
    },
    {
      title: t('components.categories.enabled', 'Visibile'),
      className: 'd-none d-sm-block text-center',
      style: { width: '14%', minWidth: '7rem' },
      sortable: true,
      sortingKey: 'enabled',
      data: ({ rowData }) => (rowData.enabled ? t('text_yes', 'Si') : t('text_no', 'No')),
    },
    {
      title: t('components.categories.details', 'Dettagli'),
      className: 'd-block d-sm-none flex-fill',
      style: { width: '100%' },
      sortable: true,
      data: ({ rowData }) => this.renderDetails(rowData),
    },
  ]

  onFilterChange = filter => this.setState({ filter })

  getData = async (index = 0, chunkSize, sortData, filter) => {
    await this.props.dispatch(getCategories({ offset: index, limit: chunkSize, sortData, filter }))
  }

  saveUser = async category => {
    category.id
      ? await this.props.dispatch(updateCategory({ category }))
      : await this.props.dispatch(createCategory({ category }))
    this.tableRef.current.refreshData()
  }

  onToggleForm = () => {
    if (this.props.show_form === true) {
      this.setState({ mode: '' })
      this.props.dispatch(resetCurrentCategory())
    }
    this.props.dispatch(toggleShowForm())
  }

  onCancel = () => {
    this.props.dispatch(resetCurrentCategory())
    this.setState({ show_modal: false })
  }

  onConfirmDelete = async () => {
    await this.props.dispatch(deleteCategory({ tenant: this.props.tenant, id: this.props.current_category.id }))
    this.tableRef.current.refreshData()
    this.setState({ show_modal: false })
  }

  componentDidMount() {}

  componentWillUnmount() {
    this.props.dispatch(resetCategories())
  }
  handleUpdateErrors = errors => {
    this.props.dispatch(updateErrors(errors))
  }

  render() {
    let { show_modal, mode, filter } = this.state
    let { current_category, show_form, total, categories, errors } = this.props

    let title = t('components.categories.create_category', 'Crea categoria')
    if (current_category.id) {
      title =
        mode === 'view'
          ? t('components.categories.view_category', 'Visualizza categoria')
          : t('components.categories.edit_category', 'Modifica categoria')
    }
    return (
      <div className="card card-body container-fluid flex-fill d-flex flex-column px-0 pb-0 pt-2 mb-0">
        <div className="d-flex flex-column flex-fill">
          <div className="toolbar px-2">
            <Button size="sm" onClick={this.onToggleForm} disabled={this.props.show_form}>
              <i className="fas fa-plus fa-fw text-start me-0 ps-1 ps-sm-0" />
              <span className="d-none d-sm-inline">
                <Trans i18nKey="buttons.add_category">Aggiungi categoria</Trans>
              </span>
            </Button>

            <div className="flex-fill">
              <SearchButton
                value={this.state.filter}
                onChange={this.onFilterChange}
                disabled={this.props.show_form}
                className="float-md-end"
              />
            </div>
          </div>

          <ResponsiveTable
            ref={this.tableRef}
            className="flex-fill"
            data={categories}
            getData={this.getData}
            totalRecords={total}
            columns={this.columns}
            showMenu={show_form}
            modalSize="md"
            menuContent={CategoryForm}
            menuContentProps={{
              title: title,
              category: current_category,
              errors: errors,
              onSave: this.saveUser,
              closeForm: this.onToggleForm,
              updateErrors: this.handleUpdateErrors,
              forceUseModal: true,
              readonly: mode === 'view',
            }}
            rowClasses={rowData => (rowData.enabled ? '' : `text-muted`)}
            filter={filter}
          />
        </div>

        <Modal backdrop="static" centered show={show_modal} onHide={this.onCancel}>
          <Modal.Header>
            <Modal.Title>
              <Trans i18nKey="components.categories.delete_account_modal.title">Elimina account</Trans>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              <Trans
                i18nKey="components.categories.delete_account_modal.description"
                values={{ category: this.props.current_category?.name }}
                defaults={`Per procedere all'eliminazione dell'utente <b>{{category}}</b>, cliccare sul bottone "Elimina"`}
                components={{ 1: <b /> }}
              />
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.onCancel} variant="secondary">
              <Trans i18nKey="buttons.cancel">Annulla</Trans>
            </Button>
            <Button onClick={this.onConfirmDelete} variant="danger">
              <Trans i18nKey="buttons.delete">Elimina</Trans>
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    )
  }
}
export default connect(mstp)(ManageCategories)
